/**
 * sgpFlowUtils.js — Pure helpers extracted from SgpFlowScreen.jsx (god-file decomposition).
 *
 * Everything here is a pure function of its inputs: no React, no context, no I/O.
 * Moved verbatim from SgpFlowScreen module scope so they can be unit-tested and
 * reused (e.g. NewResultsView, BackgroundQueryContext) without importing the screen.
 */

import { isPlayerPropMarket } from './betCardFormatters';
import * as betMath from './betMath';
import { optionPassesLineTypeFilter, countLineTypeSplits } from './lineTypeFilter';

export { optionPassesLineTypeFilter, countLineTypeSplits };

/** Leg market names for SGP filter chips (cached SGP or nlOdds SGP items) */
export function getSgpLegMarketLabels(option) {
    if (!option) return [];
    if (option._isCachedSGP && Array.isArray(option.legs)) {
        return option.legs.map(l => l.market || l.market_name).filter(Boolean);
    }
    const raw = option.raw || {};
    if (Array.isArray(raw.sgp_leg_markets) && raw.sgp_leg_markets.length) {
        return raw.sgp_leg_markets.filter(Boolean);
    }
    if (Array.isArray(raw.legs) && raw.legs.length >= 2) {
        return raw.legs.map(l => l.market || l.market_name).filter(Boolean);
    }
    return [];
}

export function optionMatchesMarketFilters(option, filterMarkets) {
    if (!filterMarkets.length) return true;
    const legMs = getSgpLegMarketLabels(option);
    const matchLegToFilter = (fm, lm) => {
        const fl = fm.toLowerCase().trim();
        const ll = (lm || '').toLowerCase().trim();
        return ll === fl || ll.includes(fl) || fl.includes(ll);
    };
    if (legMs.length > 0) {
        return filterMarkets.every(fm => legMs.some(lm => matchLegToFilter(fm, lm)));
    }
    const market = option?.raw?.market || option?.raw?.market_id || option?.title || '';
    return filterMarkets.some(fm => matchLegToFilter(fm, market));
}

export function normBookKey(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Every sportsbook name that can identify a card — chips must search all of these. */
export function collectOptionBookNames(option) {
    const names = [];
    const bo = option?.bookOdds || option?.raw?.bookOdds || {};
    names.push(...Object.keys(bo));
    const ab = option?.availableBooks || option?.raw?.availableBooks || [];
    if (Array.isArray(ab)) names.push(...ab);
    const all = option?.allBooks || option?.raw?.all_books || option?.raw?.allBooks || [];
    if (Array.isArray(all)) {
        for (const b of all) {
            names.push(typeof b === 'string' ? b : (b?.name || b?.book || b?.sportsbook));
        }
    }
    names.push(option?.raw?.sportsbook, option?.raw?.book, option?.sportsbook, option?.book);
    return names.filter(Boolean);
}

export function optionMatchesSportsbookFilters(option, filterSportsbooks) {
    if (!filterSportsbooks?.length) return true;
    const have = new Set(collectOptionBookNames(option).map(normBookKey));
    return filterSportsbooks.some((fb) => have.has(normBookKey(fb)));
}

// Helper: expand a cached-SGP payload into the `bookOdds` / `availableBooks`
// shape UnifiedBetCard expects. Mirrors the 4-tier builder inside the local
// sgp_cache code path so the background/ticker path produces renderable
// cards instead of "— / Time TBD / 0 sportsbooks / No Link Available" ghosts.
//
// Returns { bookOdds, availableBooks, parlayPrice, isRenderable } where
// `isRenderable` is false only if every tier failed AND there is no
// combined_price — callers should drop those SGPs before pushing them into
// `fetchedOptions`.
export function buildSgpBookOdds(sgp) {
    const bookOdds = {};
    const availableBooks = [];
    const parlayPrice = sgp?.combined_price || sgp?.fair_price || sgp?.price || 0;

    // Tier 1: all_books array (old = array of objects, new = array of strings)
    if (sgp?.all_books && Array.isArray(sgp.all_books)) {
        for (const book of sgp.all_books) {
            if (typeof book === 'string') {
                const bookData = sgp?.bookOdds?.[book];
                const perBookEv = bookData?.ev_percent ?? bookData?.ev ?? null;
                bookOdds[book] = {
                    price: bookData?.price || parlayPrice,
                    ev_percent: perBookEv ?? sgp.best_ev ?? sgp.ev_percent ?? sgp.estimated_ev ?? 0,
                    theoretical: bookData?.theoretical === true,
                    deep_link: bookData?.deep_link || null,
                };
                availableBooks.push(book);
            } else if (book && typeof book === 'object') {
                const bookName = book.sportsbook || book.book;
                if (bookName) {
                    bookOdds[bookName] = {
                        price: book.price || parlayPrice,
                        ev_percent: book.ev_percent || 0,
                        ev_method1_additive: book.ev_method1_additive,
                        ev_method2_multiplicative: book.ev_method2_multiplicative,
                        ev_method_used: book.ev_method_used,
                        deep_link: book.deep_link,
                    };
                    availableBooks.push(bookName);
                }
            }
        }
    }

    // Tier 2: bookOdds object (skip if Tier 1 already populated)
    if (availableBooks.length === 0 && sgp?.bookOdds && typeof sgp.bookOdds === 'object') {
        for (const [bookName, bookData] of Object.entries(sgp.bookOdds)) {
            const perBookEv = bookData?.ev_percent ?? bookData?.ev ?? null;
            bookOdds[bookName] = {
                price: bookData?.price || parlayPrice,
                ev_percent: perBookEv ?? sgp.best_ev ?? sgp.ev_percent ?? sgp.estimated_ev ?? 0,
                theoretical: bookData?.theoretical === true,
                deep_link: bookData?.deep_link || sgp.deep_link,
            };
            availableBooks.push(bookName);
        }
    }

    // Tier 3: best_book hint
    if (availableBooks.length === 0 && sgp?.best_book) {
        bookOdds[sgp.best_book] = {
            price: parlayPrice,
            ev_percent: sgp.best_ev || sgp.ev_percent || 0,
            deep_link: sgp.deep_link,
        };
        availableBooks.push(sgp.best_book);
    }

    // Tier 4: theoretical fallback using combined_price only
    if (availableBooks.length === 0 && parlayPrice) {
        const fallbackBook = sgp.sportsbook || 'Theoretical';
        bookOdds[fallbackBook] = {
            price: parlayPrice,
            ev_percent: sgp.best_ev || sgp.ev_percent || 0,
        };
        availableBooks.push(fallbackBook);
    }

    const isRenderable = availableBooks.length > 0 || (Number.isFinite(parlayPrice) && parlayPrice !== 0);
    return { bookOdds, availableBooks, parlayPrice, isRenderable };
}

// Helper: calculate combined SGP math for selected legs
export const calculateSgpMath = (selectedLegs, stake = 10, betMode = 'sgp') => {
    if (!selectedLegs || selectedLegs.length === 0) return null;
    
    // Extract probabilities and prices (use BEST odds from all books)
    const legs = selectedLegs.map((leg, idx) => {
        // Calculate best odds from all available books (same logic as LegBetCard display)
        let price = null;
        if (leg?.bookOdds && Object.keys(leg.bookOdds).length > 0) {
            const allOdds = Object.values(leg.bookOdds).filter(o => o !== null && o !== undefined);
            if (allOdds.length > 0) {
                const best = allOdds.reduce((best, current) => {
                    if (current > 0 && best > 0) return current > best ? current : best;
                    if (current < 0 && best < 0) return current > best ? current : best;
                    if (current > 0) return current;
                    if (best > 0) return best;
                    return current > best ? current : best;
                }, allOdds[0]);
                price = typeof best === 'number' ? best : parseFloat(String(best).replace(/[^-0-9.]/g, ''));
                console.log(`[calculateSgpMath] Leg ${idx}: Using best odds from ${allOdds.length} books: ${price} (from ${allOdds.join(', ')})`);
            }
        }
        // Fallback to raw price if bookOdds not available
        if (price === null) {
            price = leg?.raw?.price ?? leg?.raw?.american_price ?? null;
            console.log(`[calculateSgpMath] Leg ${idx}: Fallback to raw.price: ${price}`);
        }
        
        const aiProb = leg?.raw?.ai_prob ?? leg?.ai_prob ?? null;
        let implied = null;
        if (typeof price === 'number' && !isNaN(price)) {
            implied = price > 0 ? (100 / (price + 100)) : ((-price) / ((-price) + 100));
        }
        return { price, aiProb, implied, fixtureId: leg?.raw?.fixture_id || leg?.raw?.game_id };
    });
    
    // For SGPx mode, separate SGP legs (same fixture) from straight legs (different fixtures)
    let sgpLegs = legs;
    let straightLegs = [];
    
    if (betMode === 'sgpx' && legs.length > 1) {
        // Group legs by fixture
        const fixtureGroups = {};
        legs.forEach((leg, idx) => {
            const fixId = leg.fixtureId;
            if (!fixtureGroups[fixId]) fixtureGroups[fixId] = [];
            fixtureGroups[fixId].push({ ...leg, originalIndex: idx });
        });
        
        const fixtureIds = Object.keys(fixtureGroups);
        if (fixtureIds.length > 1) {
            // Find the largest group (SGP legs) and treat others as straight
            const largestGroup = Object.values(fixtureGroups).reduce((max, group) => 
                group.length > max.length ? group : max, []);
            sgpLegs = largestGroup;
            straightLegs = legs.filter((_, idx) => 
                !largestGroup.some(leg => leg.originalIndex === idx));
        }
    }
    
    // Calculate SGP probability (multiply SGP legs)
    const sgpProb = sgpLegs.every(l => l.aiProb) 
        ? sgpLegs.reduce((acc, l) => acc * l.aiProb, 1) 
        : null;
    
    // Calculate straight leg probabilities (individual)
    const straightProbs = straightLegs.map(l => l.aiProb).filter(p => p !== null);
    
    // Combined probability (SGP * straight legs)
    const independentProb = sgpProb && straightProbs.length > 0
        ? sgpProb * straightProbs.reduce((acc, p) => acc * p, 1)
        : sgpProb;
    
    // Calculate SGP odds
    const sgpDecimalOdds = sgpLegs.map(l => {
        if (l.price === null || isNaN(l.price)) return null;
        return betMath.americanToDecimal(l.price);
    });
    const sgpCombinedDecimal = sgpDecimalOdds.every(o => o !== null) 
        ? sgpDecimalOdds.reduce((acc, o) => acc * o, 1) 
        : null;
    
    // Debug logging for SGP calculation
    if (sgpLegs.length >= 2) {
        console.log('[calculateSgpMath] SGP Calculation:', {
            numLegs: sgpLegs.length,
            prices: sgpLegs.map(l => l.price),
            decimalOdds: sgpDecimalOdds,
            combinedDecimal: sgpCombinedDecimal
        });
    }
    
    // Calculate straight leg odds
    const straightDecimalOdds = straightLegs.map(l => {
        if (l.price === null || isNaN(l.price)) return null;
        return betMath.americanToDecimal(l.price);
    });
    const straightCombinedDecimal = straightDecimalOdds.every(o => o !== null) 
        ? straightDecimalOdds.reduce((acc, o) => acc * o, 1) 
        : null;
    
    // Total combined odds
    const combinedDecimal = sgpCombinedDecimal && straightCombinedDecimal
        ? sgpCombinedDecimal * straightCombinedDecimal
        : sgpCombinedDecimal || straightCombinedDecimal;
    
    const combinedAmerican = combinedDecimal 
        ? (combinedDecimal >= 2 ? Math.round((combinedDecimal - 1) * 100) : Math.round(-100 / (combinedDecimal - 1)))
        : null;
    
    // Payout
    const payout = combinedDecimal ? stake * combinedDecimal : null;
    const profit = payout ? payout - stake : null;
    
    // EV calculation
    const ev = (independentProb && combinedDecimal) 
        ? (independentProb * combinedDecimal * stake - stake) 
        : null;
    const evPercent = ev !== null ? ((ev / stake) * 100) : null;
    
    return {
        legs: legs.length,
        sgpLegs: sgpLegs.length,
        straightLegs: straightLegs.length,
        independentProb,
        combinedDecimal,
        combinedAmerican,
        stake,
        payout,
        profit,
        ev,
        evPercent,
        betType: betMode === 'sgpx' ? 'SGPx' : 'SGP',
        // ⭐ v1.18 P1 — provenance label. This builder math multiplies leg
        // probabilities with NO correlation adjustment (no R-factor), unlike
        // cached SGPs which are priced from real /parlay/odds market data.
        // Surfaces must label this EV so an independence-model number can't
        // masquerade as a market-correlated one.
        correlationModel: 'independent',
        evLabel: 'independent model (not market-correlated)',
    };
};

// Normalize dates in query (expand relative dates to actual dates)
export const normalizeDates = (query) => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    
    const dateReplacements = {
        'tonight': today.toISOString().split('T')[0],
        'today': today.toISOString().split('T')[0],
        'tomorrow': tomorrow.toISOString().split('T')[0],
    };
    
    let normalized = query;
    for (const [term, date] of Object.entries(dateReplacements)) {
        const regex = new RegExp(`\\b${term}\\b`, 'gi');
        if (query.toLowerCase().match(regex)) {
            // Keep the original term but add the date
            normalized = normalized.replace(regex, `${term} (${date})`);
        }
    }
    
    return normalized;
};

// Normalize team names in query (expand abbreviations to full names)
export const normalizeQuery = (query) => {
    // First normalize dates
    let normalized = normalizeDates(query);
    
    // ⭐ Fix common market keyword misspellings
    const marketTypos = {
        // Assists misspellings
        'assits': 'assists', 'assitss': 'assists', 'asists': 'assists', 'assisits': 'assists',
        'assit': 'assist', 'asist': 'assist', 'assiss': 'assists', 'asiists': 'assists',
        // Rebounds misspellings
        'reboudns': 'rebounds', 'rebouds': 'rebounds', 'rebouns': 'rebounds', 'rebonds': 'rebounds',
        'rebounsd': 'rebounds', 'rebouunds': 'rebounds', 'reobunds': 'rebounds',
        'reboudn': 'rebound', 'reboud': 'rebound', 'rebonud': 'rebound',
        // Points misspellings
        'poitns': 'points', 'piints': 'points', 'pionts': 'points', 'poinst': 'points',
        'poins': 'points', 'ponits': 'points', 'pointss': 'points',
        'poinit': 'point', 'poitn': 'point',
        // Steals misspellings
        'stelas': 'steals', 'stelals': 'steals', 'steaals': 'steals', 'staels': 'steals',
        'stealls': 'steals', 'steasl': 'steals',
        // Blocks misspellings
        'blokcs': 'blocks', 'blokcks': 'blocks', 'blcoks': 'blocks', 'blokss': 'blocks',
        'blocsk': 'blocks', 'bloocks': 'blocks',
        // Turnovers misspellings
        'turnvoers': 'turnovers', 'turnoevrs': 'turnovers', 'tunrovers': 'turnovers',
        'turnoevers': 'turnovers', 'turnovrers': 'turnovers', 'turnovesr': 'turnovers',
        // Threes misspellings
        'thress': 'threes', 'therees': 'threes', 'threees': 'threes', 'threess': 'threes',
        'thrress': 'threes', '3ss': '3s',
        // Spread misspellings
        'sperad': 'spread', 'srpead': 'spread', 'spreead': 'spread', 'sprread': 'spread',
        // Moneyline misspellings
        'moneylien': 'moneyline', 'moneylin': 'moneyline', 'monelyne': 'moneyline',
        'monyeline': 'moneyline', 'moneylinne': 'moneyline',
        // Total misspellings
        'totla': 'total', 'toatl': 'total', 'totall': 'total', 'ttoal': 'total',
        // Yards misspellings
        'yardss': 'yards', 'yarsd': 'yards', 'yardds': 'yards', 'yarrds': 'yards',
        // Touchdowns misspellings
        'touchdownss': 'touchdowns', 'touchdwons': 'touchdowns', 'toucdowns': 'touchdowns',
        'touchdons': 'touchdowns', 'touchdonws': 'touchdowns',
        // Receptions misspellings
        'recpetions': 'receptions', 'recepitons': 'receptions', 'recetpions': 'receptions',
        // Goals misspellings
        'goalls': 'goals', 'gaols': 'goals', 'goalss': 'goals',
        // Saves misspellings
        'savess': 'saves', 'sveas': 'saves', 'saevs': 'saves',
    };
    
    // Apply typo corrections (case-insensitive)
    for (const [typo, correction] of Object.entries(marketTypos)) {
        const typoRegex = new RegExp(`\\b${typo}\\b`, 'gi');
        normalized = normalized.replace(typoRegex, correction);
    }
    
    // ⭐ Fix common team name misspellings
    const teamTypos = {
        // NBA Teams
        'nuggests': 'nuggets', 'nugets': 'nuggets', 'nuggetts': 'nuggets',
        'lakerss': 'lakers', 'lakres': 'lakers',
        'celitcs': 'celtics', 'cetics': 'celtics',
        'wariors': 'warriors', 'warriros': 'warriors',
        'buckss': 'bucks', 'bucsk': 'bucks',
        'sunss': 'suns', 'suuns': 'suns',
        'knickss': 'knicks', 'knciks': 'knicks', 'nicks': 'knicks',
        'sixerss': 'sixers',
        'cavss': 'cavs', 'cavalers': 'cavaliers', 'caveliers': 'cavaliers',
        'pistonss': 'pistons', 'pistosn': 'pistons',
        'thunderr': 'thunder', 'thundr': 'thunder',
        'wolve': 'wolves', 'twolves': 'timberwolves',
        'mavss': 'mavs', 'maverick': 'mavericks',
        'grizzliess': 'grizzlies', 'grizz': 'grizzlies',
        'pels': 'pelicans',
        // NFL Teams
        'chiefss': 'chiefs', 'cheifs': 'chiefs',
        'eagless': 'eagles', 'egales': 'eagles',
        'cowboyss': 'cowboys',
        'patriotss': 'patriots', 'pats': 'patriots',
        // NHL Teams
        'bruinss': 'bruins',
        'penguinss': 'penguins', 'pens': 'penguins',
        'capitalss': 'capitals', 'caps': 'capitals',
        'avalanchee': 'avalanche', 'avs': 'avalanche',
        'hurricaness': 'hurricanes', 'canes': 'hurricanes',
    };
    
    // Apply team typo corrections
    for (const [typo, correction] of Object.entries(teamTypos)) {
        const typoRegex = new RegExp(`\\b${typo}\\b`, 'gi');
        normalized = normalized.replace(typoRegex, correction);
    }
    
    const teamMap = {
        // NBA Teams
        'warriors': 'Golden State Warriors',
        'gsw': 'Golden State Warriors',
        'lakers': 'Los Angeles Lakers',
        'lal': 'Los Angeles Lakers',
        'celtics': 'Boston Celtics',
        'bos': 'Boston Celtics',
        'heat': 'Miami Heat',
        'mia': 'Miami Heat',
        'bucks': 'Milwaukee Bucks',
        'mil': 'Milwaukee Bucks',
        'nuggets': 'Denver Nuggets',
        'den': 'Denver Nuggets',
        'suns': 'Phoenix Suns',
        'phx': 'Phoenix Suns',
        'mavs': 'Dallas Mavericks',
        'mavericks': 'Dallas Mavericks',
        'dal': 'Dallas Mavericks',
        'sixers': 'Philadelphia 76ers',
        '76ers': 'Philadelphia 76ers',
        'phi': 'Philadelphia 76ers',
        'clippers': 'Los Angeles Clippers',
        'lac': 'Los Angeles Clippers',
        'nets': 'Brooklyn Nets',
        'bkn': 'Brooklyn Nets',
        'hawks': 'Atlanta Hawks',
        'atl': 'Atlanta Hawks',
        'bulls': 'Chicago Bulls',
        'chi': 'Chicago Bulls',
        'cavs': 'Cleveland Cavaliers',
        'cavaliers': 'Cleveland Cavaliers',
        'cle': 'Cleveland Cavaliers',
        'grizzlies': 'Memphis Grizzlies',
        'mem': 'Memphis Grizzlies',
        'hornets': 'Charlotte Hornets',
        'cha': 'Charlotte Hornets',
        'jazz': 'Utah Jazz',
        'uta': 'Utah Jazz',
        'kings': 'Sacramento Kings',
        'sac': 'Sacramento Kings',
        'spurs': 'San Antonio Spurs',
        'sas': 'San Antonio Spurs',
        'thunder': 'Oklahoma City Thunder',
        'okc': 'Oklahoma City Thunder',
        'blazers': 'Portland Trail Blazers',
        'por': 'Portland Trail Blazers',
        'rockets': 'Houston Rockets',
        'hou': 'Houston Rockets',
        'pelicans': 'New Orleans Pelicans',
        'nop': 'New Orleans Pelicans',
        'knicks': 'New York Knicks',
        'nyk': 'New York Knicks',
        'raptors': 'Toronto Raptors',
        'tor': 'Toronto Raptors',
        'pacers': 'Indiana Pacers',
        'ind': 'Indiana Pacers',
        'pistons': 'Detroit Pistons',
        'det': 'Detroit Pistons',
        'magic': 'Orlando Magic',
        'orl': 'Orlando Magic',
        'wizards': 'Washington Wizards',
        'was': 'Washington Wizards',
        'timberwolves': 'Minnesota Timberwolves',
        'wolves': 'Minnesota Timberwolves',
        'min': 'Minnesota Timberwolves',
    };
    
    const queryLower = normalized.toLowerCase();
    
    // Replace team abbreviations with full names
    for (const [abbrev, fullName] of Object.entries(teamMap)) {
        // Match whole words only (with word boundaries)
        const regex = new RegExp(`\\b${abbrev}\\b`, 'gi');
        if (queryLower.match(regex)) {
            normalized = normalized.replace(regex, fullName);
            break; // Only replace first match to avoid conflicts
        }
    }
    
    return normalized;
};

// ⭐ Market relevance scoring function - used for filter ordering AND result sorting
export const getMarketRelevanceToQuery = (market, query) => {
    if (!market || !query) return 0;
    
    const marketLower = market.toLowerCase();
    const queryLower = query.toLowerCase();
    
    // Market keyword aliases for matching
    const marketKeywordsWithAliases = {
        'moneyline': ['moneyline', 'money line', 'ml', 'winner', 'to win'],
        'spread': ['spread', 'point spread', 'handicap', 'ats', 'against the spread'],
        'total': ['total', 'totals', 'over', 'under', 'o/u', 'over/under'],
        'points': ['points', 'pts', 'point'],
        'assists': ['assists', 'ast', 'assist'],
        'rebounds': ['rebounds', 'reb', 'rebs', 'rebound'],
        'threes': ['threes', '3-pointers', '3pt', '3s', 'made threes', 'three pointers'],
        'steals': ['steals', 'stl', 'steal'],
        'blocks': ['blocks', 'blk', 'block'],
        'yards': ['yards', 'passing yards', 'rushing yards', 'receiving yards', 'yds'],
        'touchdowns': ['touchdowns', 'tds', 'td', 'touchdown'],
        'goals': ['goals', 'goal', 'to score'],
        'saves': ['saves', 'save'],
        'hits': ['hits', 'hit'],
        'home runs': ['home runs', 'hr', 'homers'],
        'strikeouts': ['strikeouts', 'ks', 'so', 'strikeout'],
        'puck line': ['puck line', 'puck'],
        'run line': ['run line'],
        'team total': ['team total', 'team totals'],
        'player': ['player', 'prop', 'props']
    };
    
    // Find which market types the user is asking for
    let requestedMarkets = [];
    for (const [canonical, aliases] of Object.entries(marketKeywordsWithAliases)) {
        if (aliases.some(alias => queryLower.includes(alias))) {
            requestedMarkets.push(canonical);
        }
    }
    
    // Score this market against requested markets
    let score = 0;
    
    for (const requested of requestedMarkets) {
        const aliases = marketKeywordsWithAliases[requested] || [requested];
        
        // Score 100: Exact match or very close
        if (marketLower === requested || aliases.some(a => marketLower === a)) {
            score = Math.max(score, 100);
        }
        // Score 90: Market starts with requested term
        else if (marketLower.startsWith(requested) || aliases.some(a => marketLower.startsWith(a))) {
            score = Math.max(score, 90);
        }
        // Score 80: Plain market (no time qualifiers like "1st half")
        else if ((marketLower.includes(requested) || aliases.some(a => marketLower.includes(a))) && 
                 !/\d|half|quarter|qt|qtr|period|inning/i.test(marketLower)) {
            score = Math.max(score, 80);
        }
        // Score 50: Contains but with qualifiers
        else if (marketLower.includes(requested) || aliases.some(a => marketLower.includes(a))) {
            score = Math.max(score, 50);
        }
    }
    
    return score;
};

// Extract all player names from a selection string.
// Handles three formats:
//   H2H arrows:  "Andrew Nembhard -> Jose Alvarado -> Andrew Nembhard"
//   H2H colons:  "Ivica Zubac :: Josh Hart -- Josh Hart"
//   Standard:    "Andrew Nembhard Over 5.5 Assists"
// Returns deduplicated array of valid-looking player name strings.
export const _extractNamesFromSelection = (sel) => {
    if (!sel || typeof sel !== 'string') return [];
    const s = sel.trim();
    // Split on any H2H separator: " -> ", " :: ", " -- " (2+ dashes)
    const parts = s.split(/\s*(?:->|::|--+)\s*/);
    if (parts.length > 1) {
        // H2H format — each segment should be a player name
        const names = [];
        for (const p of parts) {
            const t = p.trim();
            // Must start with an uppercase letter, no leading digits, not an "over/under" token
            if (t.length > 2 && /^[A-Z]/.test(t) && !/^\d/.test(t) && !/^(over|under)\b/i.test(t)) {
                if (!names.includes(t)) names.push(t);
            }
        }
        return names;
    }
    // Standard Over/Under format: strip the trailing "Over/Under X.X [unit]"
    const extracted = s.replace(/\s*\b(over|under)\s*[\d.]+.*$/i, '').trim();
    if (extracted && extracted !== s && extracted.length > 2) return [extracted];
    return [];
};

// Was a hand-maintained mirror of isPlayerPropMarket ("must be kept in sync") —
// now literally the same function, so sync is guaranteed by construction.
export const _isPlayerPropMarketName = isPlayerPropMarket;

export const _isOptionPlayerProp = (o) => {
    if (o?._isCachedSGP && Array.isArray(o?.legs) && o.legs.length) {
        return o.legs.some(leg => _isPlayerPropMarketName(leg.market || leg.market_name || ''));
    }
    return (
        o?._isPlayerProp === true ||
        o?.raw?._isPlayerProp === true ||
        o?.raw?.is_player_prop === true ||
        o?.raw?._resultType === 'player_prop' ||
        o?.raw?._resultSection === 'player_props' ||
        !!(o?.raw?.player_name) ||
        !!(o?.player_name) ||
        _isPlayerPropMarketName(o?.raw?.market || o?.title || '')
    );
};

/**
 * Filter-chip facets from a results pool. Shared by production
 * (`useSgpResultFacets`) and Results Lab so chip lists stay in lockstep.
 */
export function extractResultFacets(fetchedOptions = [], resultsContextQuery = '') {
    const books = new Set();
    fetchedOptions.forEach((option) => {
        if (option?.availableBooks?.length > 0) {
            option.availableBooks.forEach((book) => books.add(book));
        } else if (option?.bookOdds && typeof option.bookOdds === 'object') {
            Object.keys(option.bookOdds).forEach((book) => books.add(book));
        } else {
            const book = option?.raw?.sportsbook || option?.raw?.book;
            if (book) books.add(book);
        }
    });
    const availableSportsbooks = Array.from(books).sort();

    const markets = new Set();
    fetchedOptions.forEach((option) => {
        const legMs = getSgpLegMarketLabels(option);
        legMs.forEach((m) => { if (m) markets.add(m); });
        const market = option?.raw?.market || option?.raw?.market_id || option?.title;
        const genericSgp = /^\d+-Leg(\s+SGP)?$/i.test(String(market || '').trim())
            || /^\d+-Leg Same Game Parlay$/i.test(String(market || '').trim());
        if (market && (legMs.length === 0 || !genericSgp)) {
            markets.add(market);
        }
    });
    const availableMarkets = Array.from(markets).sort((a, b) => {
        const scoreA = getMarketRelevanceToQuery(a, resultsContextQuery);
        const scoreB = getMarketRelevanceToQuery(b, resultsContextQuery);
        if (scoreA !== scoreB) return scoreB - scoreA;
        return a.localeCompare(b);
    });

    const playerMap = new Map();
    fetchedOptions.forEach((option) => {
        if (option?._isCachedSGP && Array.isArray(option?.legs)) {
            option.legs.forEach((leg) => {
                const explicitName = (leg.player_name || '').trim();
                const names = explicitName
                    ? [explicitName]
                    : _extractNamesFromSelection(leg.name || leg.selection || '');
                for (const rawName of names) {
                    const trimmed = rawName.trim();
                    if (!trimmed || trimmed.length < 3) continue;
                    if (/[+-]\d+\.?\d*$/.test(trimmed)) continue;
                    if (/^(over|under)\s/i.test(trimmed)) continue;
                    if (!playerMap.has(trimmed)) playerMap.set(trimmed, null);
                }
            });
            return;
        }
        const raw = option?.raw || {};
        const market = raw?.market || option?.title || '';
        const isPlayerProp =
            option?._isPlayerProp === true ||
            raw?._isPlayerProp === true ||
            raw?.is_player_prop === true ||
            raw?._resultType === 'player_prop' ||
            raw?._resultSection === 'player_props' ||
            !!(raw?.player_name) ||
            !!(option?.player_name) ||
            _isPlayerPropMarketName(market);
        if (!isPlayerProp) return;
        const explicitName = (option?.player_name || raw?.player_name || raw?.player || '').trim();
        const names = explicitName
            ? [explicitName]
            : _extractNamesFromSelection(raw?.selection || '');
        if (names.length === 0) return;
        const headshot = option?._player_headshot || option?._headshot_url ||
                         raw?._player_headshot || raw?._headshot_url || null;
        for (const rawName of names) {
            const trimmed = rawName.trim();
            if (!trimmed || trimmed.length < 3) continue;
            if (/[+-]\d+\.?\d*$/.test(trimmed)) continue;
            if (/^(over|under)\s/i.test(trimmed)) continue;
            if (!playerMap.has(trimmed)) playerMap.set(trimmed, headshot);
        }
    });
    const availablePlayers = Array.from(playerMap.entries())
        .map(([name, headshotUrl]) => ({ name, headshotUrl }))
        .sort((a, b) => a.name.localeCompare(b.name));

    const hasTeamResults = fetchedOptions.some((o) => !_isOptionPlayerProp(o));
    const hasPropsResults = fetchedOptions.some((o) => _isOptionPlayerProp(o));
    const isSgpResults = fetchedOptions.length > 0 && fetchedOptions[0]?._isCachedSGP === true;

    const counts = new Set();
    if (isSgpResults) {
        fetchedOptions.forEach((opt) => {
            const lc = opt?.leg_count || opt?._sgpData?.leg_count || opt?.legs?.length;
            if (lc) counts.add(Number(lc));
        });
    }
    const availableLegCounts = Array.from(counts).sort((a, b) => a - b);

    return {
        availableSportsbooks,
        availableMarkets,
        availablePlayers,
        availableLegCounts,
        hasTeamResults,
        hasPropsResults,
        isSgpResults,
    };
}
